import React, { useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { AlertCircleIcon, ArrowLeftIcon, CheckCircleIcon, XIcon, CalendarIcon } from 'lucide-react';
interface BulkPaymentPreviewProps {
  data: any[];
  onBack: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}
export function BulkPaymentPreview({
  data,
  onBack,
  onConfirm,
  isSubmitting
}: BulkPaymentPreviewProps) {
  const {
    t
  } = useTranslation('payments');
  const [selectedRows, setSelectedRows] = useState<number[]>([...Array(data.length).keys()]);
  const toggleRow = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter(i => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };
  const toggleAllRows = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...Array(data.length).keys()]);
    }
  };
  const hasErrors = data.some(row => {
    return !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
  });
  // Calculate totals
  const selectedData = data.filter((_, index) => selectedRows.includes(index));
  const totalAmount = selectedData.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const currency = data[0]?.currency || getTenant().currency.code;
  return <div className="space-y-6" data-id="element-7784" data-mp="55rhm6l06f1c">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-7785" data-mp="9969buib0jum">
        <div className="flex justify-between items-center mb-6" data-id="element-7786" data-mp="9738sgjiz8bn">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-7787" data-mp="e6zh2rz99lx0">
            {t('components.BulkPaymentPreview.preview_batch')}
          </h3>
        </div>
        {hasErrors && <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-start mb-6" data-id="element-7788" data-mp="2fyucry549ftl">
            <AlertCircleIcon size={18} className="mt-0.5 mr-3 flex-shrink-0" data-id="element-7789" data-mp="3ja1eflzvzqvk" />
            <div data-id="element-7790" data-mp="3ebgfdpwdfd8p">
              <p className="text-sm font-medium" data-id="element-7791" data-mp="3ebzwx5i3zzm7">
                {t('components.BulkPaymentPreview.some_rows_have_errors')}
              </p>
              <p className="text-sm mt-1" data-id="element-7792" data-mp="3scstdvrz14ry">
                {t('components.BulkPaymentPreview.please_fix_the_issues_highlighted_in')}
              </p>
            </div>
          </div>}
        <div className="border border-gray-200 rounded-md overflow-hidden mb-6" data-id="element-7793" data-mp="61omogucbwcv">
          <div className="max-h-[400px] overflow-auto" data-id="element-7794" data-mp="12u51i840duc">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-7795" data-mp="ewl6s4flmtfi">
              <thead className="bg-gray-50 sticky top-0" data-id="element-7796" data-mp="auq714dbgo3u">
                <tr data-id="element-7797" data-mp="av9jw6o40mb0">
                  <th className="px-4 py-3 text-left" data-id="element-7798" data-mp="ax3wmxp89840">
                    <div className="flex items-center" data-id="element-7799" data-mp="ovkbxd15o6xr">
                      <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.length === data.length} onChange={toggleAllRows} data-id="element-7800" data-mp="jvfyxwzghuca" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7801" data-mp="tiik1m258l8a">
                    {t('components.BulkPaymentPreview.customer')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7802" data-mp="kpf6ldri08dn">
                    {t('components.BulkPaymentPreview.contact')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7803" data-mp="audgofpef2a0">
                    {t('components.BulkPaymentPreview.amount')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7804" data-mp="1snp99vio3c8">
                    {t('components.BulkPaymentPreview.description')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7805" data-mp="3uxq6cycoxxw9">
                    {t('components.BulkPaymentPreview.reference')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-7806" data-mp="3g7iz7es4w1av">
                {data.map((row, index) => {
                const hasError = !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
                return <tr key={index} className={hasError ? 'bg-red-50' : ''} data-id="element-7807" data-mp="2x9g4cjcwna55" data-mpi={index}>
                      <td className="px-4 py-3" data-id="element-7808" data-mp="10ek0fp38jb3l">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.includes(index)} onChange={() => toggleRow(index)} disabled={hasError} data-id="element-7809" data-mp="10d8xfrhm67dj" />
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.customerName ? 'text-red-600' : 'text-gray-900'}`} data-id="element-7810" data-mp="1cs6y70rzkgnt">
                        {row.customerName || 'Missing name'}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-7811" data-mp="p1baaeqni5lu">
                        <div className={!row.email ? 'text-red-600' : 'text-gray-900'} data-id="element-7812" data-mp="tzcreu4inuuo">
                          {row.email || 'Missing email'}
                        </div>
                        {row.phone && <div className="text-gray-500 text-xs mt-1" data-id="element-7813" data-mp="2ad37kz8wu9ko">
                            {row.phone}
                          </div>}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium ${!row.amount || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-7814" data-mp="1fqvz8p3jnfi8">
                        {!row.amount || isNaN(parseFloat(row.amount)) ? 'Invalid amount' : parseFloat(row.amount) <= 0 ? 'Amount must be > 0' : formatCurrencyAmount(parseFloat(row.amount), {
                      currency: row.currency || currency,
                      fractionDigits: 2
                    })}
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.description ? 'text-red-600' : 'text-gray-500'}`} data-id="element-7815" data-mp="j7olojex5l1c">
                        {row.description || 'Missing description'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" data-id="element-7816" data-mp="2puxezw397ya7">
                        {row.reference || '-'}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-4 py-3 px-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-7817" data-mp="2ky6yj7njf0o9">
          <CalendarIcon size={14} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-7818" data-mp="38lf5rv4j0jsx" />

          <span data-id="element-7819" data-mp="2zmjw2929wrkv">
            {t('components.BulkPaymentPreview.payment_links_will_expire_in')}{' '}
            <span className="font-medium text-gray-700" data-id="element-7820" data-mp="33nvftxyguil5">
              {t('components.BulkPaymentPreview.30_days')}
            </span>{' '}
            {t('components.BulkPaymentPreview.from_creation')}
          </span>
        </div>

        <div className="flex justify-between items-center" data-id="element-7821" data-mp="1sa1cmhkqz5mu">
          <div data-id="element-7822" data-mp="2jwjlhum16g2s">
            <p className="text-sm text-gray-600" data-id="element-7823" data-mp="2jx2swhe21m2k">
              <span className="font-medium" data-id="element-7824" data-mp="2jxupnvje1puf">{selectedRows.length}</span>{' '}
              {t('components.BulkPaymentList.of')}{' '}
              <span className="font-medium" data-id="element-7825" data-mp="2avlf55o4dwh1">{data.length}</span>{' '}
              {t('components.BulkPaymentPreview.payment_requests_selected')}
            </p>
            <p className="text-sm font-medium text-gray-800 mt-1" data-id="element-7826" data-mp="25ymmkrk3txky">
              {t('components.BulkPaymentPreview.total')}{' '}
              {formatCurrencyAmount(totalAmount, {
              currency,
              fractionDigits: 2
            })}
            </p>
          </div>
          <div className="flex space-x-4" data-id="element-7827" data-mp="2tt66cjecx74t">
            <button type="button" onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-7828" data-mp="2ovnwmqsimnvr">
              {t('components.BulkPaymentPreview.back')}
            </button>
            <button type="button" onClick={onConfirm} disabled={selectedRows.length === 0 || hasErrors || isSubmitting} className={`
                px-4 py-2 font-medium rounded-lg transition-colors
                ${selectedRows.length === 0 || hasErrors || isSubmitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#E2231A] hover:bg-[#C41B14] text-white'}
              `} data-id="element-7829" data-mp="3ovpwftey2v4m">
              {isSubmitting ? 'Creating Batch...' : 'Create Payment Requests'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}